/**
 * One in-flight save. A newer draft replaces anything still waiting.
 * The queue does not merge payloads; the caller builds each full draft.
 */
export function createLatestDraftPersistQueue<T>(options: {
  save: (draft: T) => Promise<void>;
  onIdle?: () => Promise<void> | void;
}): {
  enqueue: (draft: T) => Promise<void>;
  settle: () => Promise<void>;
} {
  let pending: { draft: T; waiters: Array<(error: unknown) => void> } | null = null;
  let running = false;
  let idleWaiters: Array<() => void> = [];

  const notifyIdle = () => {
    if (running || pending) return;
    const waiters = idleWaiters;
    idleWaiters = [];
    for (const waiter of waiters) waiter();
  };

  const pump = () => {
    if (running || !pending) {
      notifyIdle();
      return;
    }
    const job = pending;
    pending = null;
    running = true;
    void (async () => {
      let error: unknown;
      try {
        await options.save(job.draft);
        if (!pending) await options.onIdle?.();
      } catch (err) {
        error = err;
      } finally {
        running = false;
        for (const waiter of job.waiters) waiter(error);
        pump();
      }
    })();
  };

  return {
    enqueue(draft) {
      return new Promise((resolve, reject) => {
        const waiter = (error: unknown) => {
          if (error) reject(error);
          else resolve();
        };
        if (pending) {
          pending.draft = draft;
          pending.waiters.push(waiter);
        } else {
          pending = { draft, waiters: [waiter] };
        }
        pump();
      });
    },
    settle() {
      return new Promise((resolve) => {
        if (!running && !pending) {
          resolve();
          return;
        }
        idleWaiters.push(resolve);
      });
    },
  };
}

export function offerDraftSaveErrorMessage(err: unknown): string {
  if (err instanceof TypeError) {
    return 'Verbindung unterbrochen. Der Entwurf wurde nicht gespeichert.';
  }
  if (err instanceof Error && err.message.trim()) return err.message;
  return 'Angebotsentwurf konnte nicht gespeichert werden.';
}
